import React from "react";
import { Link } from "react-router";
import { SweetAlertBase } from "./ConfirmDelete";
import type { DeleteReferrer } from "../../../shared/apiTypes";

/**
 * SCRUM-2352 (slice 2 of SCRUM-2351): the pop-up for a delete the server refused
 * because other documents still point at the item — the 409 body SCRUM-2350
 * defines, `{ errors: [{ msg }], referrers: [{ kind, _id, title, detail }] }`.
 *
 * One line per referrer, with a link to the list screen where the editor can
 * find and delete it by its title. The links open the list, not the single
 * item: the list screens are the only routes every kind has.
 *
 * Built on ConfirmDelete's `SweetAlertBase` so the two look alike and the
 * library's type suppression is not copied again. It knows nothing about the screen that opened it, so every
 * refusing delete (products, pages, posts, … — later slices of SCRUM-2351) can
 * reuse it as is.
 */
// Partial: a kind without a list screen would show without a link.
const REFERRER_LIST_ROUTES: Partial<Record<DeleteReferrer["kind"], string>> = {
  product: "/admin/globalproducts",
  page: "/admin/display-pages",
  stagedEdit: "/admin/updated-pages",
  translation: "/admin/translate-pages",
  // SCRUM-2363: Manage Categories edits subcategories; Site Categories opens a
  // category's score configuration through its Scoring button.
  subcategory: "/admin/category/update",
  scoreConfig: "/admin/category/site",
  // SCRUM-2355: a refused blog delete lists its posts.
  post: "/admin/posts",
  // SCRUM-2356: a refused client delete's ads, a refused design delete's
  // templates and sites. `site` also lines up SCRUM-2373's category refusal.
  ad: "/admin/ads",
  template: "/admin/template",
  site: "/admin/sites",
};

export type ReferrersDialogProps = {
  /** the server's refusal message, `errors[0].msg` of the 409 body */
  message: string;
  /** the 409 body's `referrers` */
  referrers: DeleteReferrer[];
  /** clears the dialog; called by its one "Close" button */
  onClose: () => void;
};

const ReferrersDialog = ({ message, referrers, onClose }: ReferrersDialogProps) => (
  <SweetAlertBase
    title="This item cannot be deleted yet"
    customIcon={
      <div
        className="swal2-icon swal2-warning"
        style={{ display: "flex" }}
      >
        <span className="swal2-icon-text">!</span>
      </div>
    }
    onConfirm={onClose}
    onCancel={onClose}
    confirmBtnText="Close"
  >
    <p data-testid="referrers-message">{message}</p>
    <ul className="text-left" data-testid="referrers-list">
      {referrers.map((referrer) => {
        // An unknown kind has no list screen: title and detail, no link.
        const route = REFERRER_LIST_ROUTES[referrer.kind];
        return (
          <li key={`${referrer.kind}:${referrer._id}`}>
            {route ? <Link to={route} onClick={onClose}>{referrer.title}</Link> : referrer.title}
            {referrer.detail ? ` — ${referrer.detail}` : null}
          </li>
        );
      })}
    </ul>
  </SweetAlertBase>
);

export default ReferrersDialog;
